import { useState } from 'react';
import '../../styles/theme.css';

/**
 * How to play, shown on the host screen before a round starts.
 *
 * The host reads it aloud to the room, then skips it. It is always skippable
 * and never blocks: "Skip" and "Start the round" both begin play immediately.
 */

const RULES = {
  soundoff: {
    name: 'Sound Off',
    line: 'Answer someone else’s prompt with two sounds.',
    steps: [
      ['Write a prompt', 'Everyone types one prompt. They get shuffled and handed out, so you answer somebody else’s.'],
      ['Pick two sounds', 'Search the library and choose two clips, in order. The pair is your answer — the join between them is the joke.'],
      ['Listen', 'The host screen plays two players’ answers back to back. Phones stay quiet; this screen is the speaker.'],
      ['Vote', 'Everyone except the two duellists votes for the better pair.'],
    ],
    scoring: 'Winning a duel scores. The prompt’s author also scores, more the closer the vote was — a prompt that splits the room is worth the most.',
  },
  namethatnoise: {
    name: 'Name That Noise',
    line: 'Bluff your friends about what a sound is.',
    steps: [
      ['Hear the sound', 'The host plays one mystery clip. Nobody sees its name.'],
      ['Write a bluff', 'Type what you claim the sound is. Make it plausible enough to fool someone.'],
      ['Pick the real one', 'All the bluffs and the true answer appear together, shuffled. Find the truth. You cannot vote for your own bluff.'],
    ],
    scoring: 'Points for finding the truth, and points for every player your bluff fooled.',
  },
  drift: {
    name: 'Drift',
    line: 'Audio telephone. Watch a sound become something else.',
    steps: [
      ['Start a chain', 'Everyone writes a short phrase.'],
      ['Pass it on', 'You receive somebody’s phrase and answer it with a sound. The next player hears only that sound and writes what they think it means.'],
      ['Keep drifting', 'Text, sound, text, sound — each player sees only the step before theirs.'],
      ['The reveal', 'Every chain is played back from the beginning so the room can watch it come apart.'],
    ],
    scoring: 'Vote for the chain that drifted furthest, and for the single step that broke it.',
  },
  foley: {
    name: 'Foley',
    line: 'Build a scene out of sound effects.',
    steps: [
      ['Set the scene', 'Everyone writes a one-line scene, and they get dealt out to other players.'],
      ['Score it', 'Lay exactly three clips in order to tell that scene in sound — the gaps between them are yours to set.'],
      ['Play it back', 'The host plays each production for the room, start to finish.'],
      ['Vote', 'Pick the production that best told its scene.'],
    ],
    scoring: 'Points for winning your scene, and for writing a scene people enjoyed scoring.',
  },
};

export function HowToPlay({ mode, onSkip, onBack, onStart, playerCount = 0, minPlayers = 0 }) {
  const rules = RULES[mode];
  if (!rules) return null;

  const short = minPlayers > 0 && playerCount < minPlayers;
  const handleBack = onBack || onSkip;

  return (
    <div
      data-testid="how-to-play"
      data-mode={mode}
      style={{
        border: '1px solid var(--sd-line-3, #1F252C)',
        borderRadius: '16px',
        background: 'var(--sd-panel, #0D1015)',
        padding: 'clamp(24px, 3vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '28px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div
            style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '12px',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--sd-dim, #6E7982)',
              marginBottom: '10px',
            }}
          >
            How to play
          </div>
          <h2 style={{ margin: 0, fontSize: 'clamp(28px, 3.6vw, 44px)', fontWeight: 800, letterSpacing: '-0.025em' }}>
            {rules.name}
          </h2>
          <p style={{ margin: '10px 0 0', fontSize: 'clamp(16px, 1.6vw, 20px)', color: 'var(--sd-ink-2, #C6CDD4)' }}>
            {rules.line}
          </p>
        </div>

        <button
          type="button"
          data-testid="how-to-play-back-btn"
          onClick={handleBack}
          style={{
            minHeight: '40px',
            padding: '0 18px',
            borderRadius: '10px',
            border: '1px solid var(--sd-line-2, #1C2229)',
            background: 'var(--sd-panel-2, #0E1116)',
            color: 'var(--sd-ink-2, #C6CDD4)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.15s ease',
          }}
        >
          <span>←</span>
          <span>Back to Host Screen</span>
        </button>
      </div>

      <ol
        data-testid="how-to-play-steps"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(rules.steps.length, 4)}, minmax(0, 1fr))`,
          gap: '18px',
        }}
      >
        {rules.steps.map(([title, body], i) => (
          <li
            key={title}
            style={{
              borderTop: '2px solid var(--sd-teal, #3ACFCF)',
              paddingTop: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                color: 'var(--sd-teal, #3ACFCF)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span style={{ fontSize: '17px', fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
            <span style={{ fontSize: '14.5px', lineHeight: 1.5, color: 'var(--sd-muted, #79838D)' }}>{body}</span>
          </li>
        ))}
      </ol>

      <div
        style={{
          borderLeft: '2px solid var(--sd-amber, #E0A458)',
          paddingLeft: '16px',
          fontSize: '14.5px',
          lineHeight: 1.55,
          color: 'var(--sd-ink-2, #C6CDD4)',
        }}
      >
        <strong style={{ color: 'var(--sd-amber, #E0A458)', fontWeight: 700 }}>Scoring. </strong>
        {rules.scoring}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="how-to-play-start"
          onClick={onStart}
          disabled={short}
          style={{
            minHeight: 'var(--sd-tap-cta, 56px)',
            padding: '0 32px',
            borderRadius: '13px',
            border: 'none',
            background: short ? 'var(--sd-panel-2, #0E1116)' : 'var(--sd-teal, #3ACFCF)',
            color: short ? 'var(--sd-dim, #6E7982)' : 'var(--sd-ground, #08090B)',
            fontSize: '16.5px',
            fontWeight: 700,
            cursor: short ? 'not-allowed' : 'pointer',
          }}
        >
          {short ? `Needs ${minPlayers} players` : 'Start the round'}
        </button>

        <button
          type="button"
          data-testid="how-to-play-skip"
          onClick={handleBack}
          style={{
            minHeight: 'var(--sd-tap-min, 44px)',
            padding: '0 20px',
            borderRadius: '11px',
            border: '1px solid var(--sd-line-2, #1C2229)',
            background: 'transparent',
            color: 'var(--sd-muted, #79838D)',
            fontSize: '14.5px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>←</span>
          <span>Back to Host Screen</span>
        </button>

        <span style={{ fontSize: '13px', color: 'var(--sd-dim, #6E7982)' }}>
          Everyone can read along on their phone.
        </span>
      </div>
    </div>
  );
}

/** Remembers, per browser, which modes the host has already been shown. */
export function useHowToPlay(mode) {
  const key = `sounddrift:howto:${mode}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const dismiss = () => {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* a host with storage disabled just sees it again; harmless */
    }
    setDismissed(true);
  };
  return { dismissed, dismiss };
}

export default HowToPlay;
